/**
 * Seed `parent_organizations` from the `lookup_parent_organization` lookup.
 *
 * A dry run first, always: the plan is built from the LIVE lookup the moment the
 * dialog opens, and nothing is written until the curator has seen exactly which
 * documents will be created. The four entries the design pass suspected are not
 * parent organizations start UNTICKED with the reason shown — creating one is an
 * explicit decision.
 *
 * Create-only and idempotent: an id that already has a document is listed and
 * left alone, so running the seed again creates nothing and overwrites nothing.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  createParentOrganization,
  getLookupCollectionFull,
  getParentOrganizations,
  invalidateLookupCache,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { planParentOrgSeed, type ParentOrgSeedPlan, type ParentOrgUsage } from './parentOrgSeed';

const LOOKUP = 'lookup_parent_organization';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Raw `parentOrg` usage, or null when the counts could not be loaded. */
  usage: ParentOrgUsage | null;
  onSeeded: () => void;
}

export function SeedFromLookupDialog({ isOpen, onClose, usage, onSeeded }: Props) {
  const [plan, setPlan] = useState<ParentOrgSeedPlan | null>(null);
  const [lookupCount, setLookupCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPlan(null);
    setLoadError(null);
    // Bypass the session lookup cache: a seed must run against what the lookup
    // holds NOW, not a copy up to five minutes old.
    invalidateLookupCache();
    Promise.all([getLookupCollectionFull(LOOKUP), getParentOrganizations()])
      .then(([lookup, existing]) => {
        if (cancelled) return;
        if (!lookup) {
          setLoadError(`The ${LOOKUP} lookup could not be read.`);
          return;
        }
        const next = planParentOrgSeed(
          lookup.values,
          new Set(existing.map((o) => o.id)),
          usage ?? undefined,
        );
        setLookupCount(lookup.values.length);
        setPlan(next);
        setSelected(
          new Set(next.toCreate.filter((e) => !e.suspectedMisclassification).map((e) => e.id)),
        );
      })
      .catch((err) => {
        console.error('Failed to plan the parent-org seed:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not load the lookup');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, usage]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSeed = async () => {
    if (!plan) return;
    const entries = plan.toCreate.filter((e) => selected.has(e.id));
    setRunning(true);
    let created = 0;
    let existed = 0;
    let failed = 0;
    for (const entry of entries) {
      try {
        const wrote = await createParentOrganization(entry.id, {
          name: entry.name,
          displayName: entry.displayName,
          ...(entry.description ? { description: entry.description } : {}),
          alternativeNames: entry.alternativeNames,
          status: 'active',
        });
        if (wrote) created += 1;
        else existed += 1;
      } catch (err) {
        console.error(`Seed failed for ${entry.id}:`, err);
        failed += 1;
      }
    }
    setRunning(false);
    const parts = [`${created} created`];
    if (existed) parts.push(`${existed} already existed`);
    if (failed) parts.push(`${failed} failed`);
    if (failed) toast.error(parts.join(', '));
    else toast.success(parts.join(', '));
    onSeeded();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (running ? undefined : onClose())}
      title="Seed parent organizations from the lookup"
      size="xl"
    >
      <div className="space-y-5">
        {loadError && (
          <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{loadError}</p>
        )}
        {!plan && !loadError && <p className="text-sm text-gray-500">Reading the lookup…</p>}

        {plan && (
          <>
            <p className="text-sm text-gray-600">
              Dry run of <span className="font-medium">{lookupCount}</span> lookup entries. Nothing is
              written until you press Create. Existing documents are never overwritten, and brand and
              manufacturer <code>parentOrg</code> fields are not touched.
            </p>

            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Would create ({plan.toCreate.length})
              </h4>
              {plan.toCreate.length === 0 ? (
                <p className="text-xs text-gray-400">Nothing — every lookup entry already has a document.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                      <tr>
                        <th className="w-10 px-3 py-2" />
                        <th className="px-3 py-2">Id</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Alternative names (for Phase 3 matching)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {plan.toCreate.map((entry) => (
                        <tr
                          key={entry.id}
                          className={entry.suspectedMisclassification ? 'bg-amber-50/60' : undefined}
                        >
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              aria-label={`Seed ${entry.id}`}
                              checked={selected.has(entry.id)}
                              disabled={running}
                              onChange={() => toggle(entry.id)}
                            />
                          </td>
                          <td className="px-3 py-2 align-top font-mono text-xs text-gray-700">{entry.id}</td>
                          <td className="px-3 py-2 align-top">
                            <div className="text-gray-800">{entry.displayName}</div>
                            {entry.sourceValues.length > 1 && (
                              <div className="text-[11px] text-indigo-700">
                                Merges {entry.sourceValues.length} lookup entries
                              </div>
                            )}
                            {entry.suspectedMisclassification && (
                              <div className="mt-0.5 text-[11px] text-amber-800">
                                ⚠ {entry.suspectedMisclassification} Left unticked — tick to seed it
                                anyway, or leave it to reroute to the right collection.
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top text-xs text-gray-600">
                            {entry.alternativeNames.join(' · ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {plan.alreadyExists.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Already exist — left alone ({plan.alreadyExists.length})
                </h4>
                <p className="text-xs text-gray-600">{plan.alreadyExists.map((e) => e.id).join(', ')}</p>
              </div>
            )}

            {plan.unusable.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Skipped — nothing to make an id from ({plan.unusable.length})
                </h4>
                <p className="text-xs text-gray-600">
                  {plan.unusable.map((lv) => lv.id || '(no id)').join(', ')}
                </p>
              </div>
            )}

            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                In use on brands or manufacturers, but not in the lookup (
                {usage ? plan.usedButNotInLookup.length : '?'})
              </h4>
              {!usage ? (
                <p className="text-xs text-amber-700">
                  Usage counts couldn't be loaded, so this list is unavailable.
                </p>
              ) : plan.usedButNotInLookup.length === 0 ? (
                <p className="text-xs text-gray-400">None — every string in use is covered.</p>
              ) : (
                <>
                  <p className="mb-1 text-xs text-gray-600">
                    Not seeded here. Phase 3's backfill has to account for each of these.
                  </p>
                  <ul className="text-xs text-gray-700">
                    {plan.usedButNotInLookup.map((u) => (
                      <li key={u.value}>
                        <span className="font-mono">{u.value}</span>
                        <span className="text-gray-400">
                          {' '}
                          — {u.brands} brand{u.brands === 1 ? '' : 's'}, {u.manufacturers} manufacturer
                          {u.manufacturers === 1 ? '' : 's'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button variant="secondary" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSeed()}
            loading={running}
            disabled={!plan || selected.size === 0 || running}
          >
            Create {selected.size} organization{selected.size === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
